<script setup lang="ts">
import { watch, computed } from 'vue';
import { useRecipeImageUrl } from '@shared/app/composables/useRecipeImageUrl';
import { definePageMeta } from '#imports';
import { useRoute } from '#app/composables/router';
import { useState } from '#app/composables/state';
import { useSeoMeta } from '#app/composables/head';
import type { RecipeDetailResponse } from '~~/server/api/kiwi_user/recipes/detail';
import {
  recipeDetailMiddleware,
  RECIPE_DETAIL_STATE_KEY,
} from '~/middleware/recipeDetail';
import { useShowHistory } from '~/composables/useShowHistory';
import { useRecipeJsonLd } from '~/composables/useRecipeJsonLd';

definePageMeta({
  layout: 'recipe',
  middleware: recipeDetailMiddleware,
});

const route = useRoute('public-article-recipe-old_identifier');

const stateKey = `${RECIPE_DETAIL_STATE_KEY}-${route.params.old_identifier}`;
const data = useState<RecipeDetailResponse | null>(stateKey, () => null);

const { createRecipeMainImageUrl } = useRecipeImageUrl();

// JSON-LD用の複数サイズの画像URLを生成
const mainImageUrlsForJsonLd = computed(() => {
  if (!data.value?.main_image) {
    return [];
  }
  // schema.orgのRecipeでは異なるアスペクト比の画像を推奨
  // @see https://developers.google.com/search/docs/appearance/structured-data/recipe#recipe-properties
  return [
    createRecipeMainImageUrl({
      recipeId: data.value.id,
      filename: data.value.main_image,
      w: 250,
      h: 250, // 1:1
    }),
    createRecipeMainImageUrl({
      recipeId: data.value.id,
      filename: data.value.main_image,
      w: 400,
      h: 300, // 4:3
    }),
    createRecipeMainImageUrl({
      recipeId: data.value.id,
      filename: data.value.main_image,
      w: 800,
      h: 450, // 16:9
    }),
  ];
});

// SEO設定
useSeoMeta({
  title: data.value?.title ?? '',
  description: data.value?.lead ?? '',
});

useRecipeJsonLd(data, mainImageUrlsForJsonLd);

// 閲覧履歴保存（localStorageを使用するためクライアントサイドでのみ実行）
// NOTE: async setupではawaitの後にonMountedを登録するとinstance unavailableになるため、
// ライフサイクルフックを使わずwatchでデータが揃ったタイミングで保存する
if (import.meta.client) {
  const { saveShowHistory } = useShowHistory('recipe');
  watch(
    () => data.value?.id,
    (id) => {
      if (id) {
        saveShowHistory(id);
      }
    },
    { immediate: true },
  );
}
</script>

<template>
  <OrganismsRecipeDetailPage :recipe-data="data" />
</template>
